import React, { useState, useMemo } from "react";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { useForm, Controller } from "react-hook-form";
import validator from "validator";
import styles from "./InitialForm.module.css";
import useGeoLocation from "@/services/useGeoLocation";

interface FormData {
  name: string;
  email: string;
  phone: string;
}

interface InitialFormProps {
  onSubmit: (data: FormData) => Promise<void>;
  isSubmitting: boolean;
  mobileNumber: string;
  setMobileNumber: (number: string) => void;
  handleCountryCodeChange: (country: any) => void;
  phoneNumberError: string;
  setPhoneNumberError: (error: string) => void;
  isFocused: boolean;
  setIsFocused: (focused: boolean) => void;
  checkExistingUser: (phoneNumber: string, email: string) => Promise<boolean>;
  title?: string;
}

const InitialForm: React.FC<InitialFormProps> = ({
  onSubmit,
  isSubmitting,
  mobileNumber,
  setMobileNumber,
  handleCountryCodeChange,
  phoneNumberError,
  setPhoneNumberError,
  setIsFocused,
  title,
}) => {
  const [touchedFields, setTouchedFields] = useState({
    name: false,
    email: false,
    phone: false,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    trigger,
    watch,
  } = useForm<FormData>({
    mode: "onChange",
    reValidateMode: "onChange",
  });

  // Watch all form fields for real-time validation
  const formValues = watch();

  // Memoize validation state to prevent unnecessary re-renders
  const isFormValid = useMemo(() => {
    const hasValidationErrors =
      Object.keys(errors).length > 0 || !!phoneNumberError;
    const isAllFieldsFilled = Boolean(
      formValues.name?.trim() &&
        formValues.email?.trim() &&
        mobileNumber?.trim()
    );
    return !hasValidationErrors && isAllFieldsFilled;
  }, [errors, phoneNumberError, formValues, mobileNumber]);

  const validatePhoneNumber = (
    value: string | undefined,
    isTouched: boolean
  ) => {
    if (!isTouched) return true;

    if (!value?.trim()) {
      setPhoneNumberError("Phone number is required");
      return false;
    }

    if (!validator.isMobilePhone(value)) {
      setPhoneNumberError("Please enter a valid phone number");
      return false;
    }

    setPhoneNumberError("");
    return true;
  };

  // Optimize error display logic
  const getErrorMessage = () => {
    if (!Object.values(touchedFields).some((touched) => touched)) {
      return null;
    }

    if (
      Object.keys(errors).length > 1 &&
      !mobileNumber?.trim() &&
      touchedFields.phone
    ) {
      return "Required: All fields must be filled";
    }

    if (errors?.name && touchedFields.name) {
      return errors.name.message;
    }

    if (errors?.email && touchedFields.email) {
      return errors.email.message;
    }

    if (phoneNumberError && touchedFields.phone) {
      return phoneNumberError;
    }

    if (errors?.phone && touchedFields.phone) {
      return errors.phone.message;
    }

    return null;
  };

  const errorMessage = getErrorMessage();

  const geoLocationData: any = useGeoLocation();

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
      <h2>{title}</h2>

      <div className={styles.field}>
        <input
          {...register("name", {
            required: "Name is required",
            pattern: {
              value: /^[A-Za-z\s]+$/,
              message: "Only alphabets are allowed",
            },
            onChange: () =>
              setTouchedFields((prev) => ({ ...prev, name: true })),
          })}
          placeholder="Enter Full Name"
          autoComplete="off"
        />
      </div>

      <div className={styles.field}>
        <input
          {...register("email", {
            required: "Email is required",
            validate: (value) =>
              validator.isEmail(value) || "Invalid email address",
            onChange: () =>
              setTouchedFields((prev) => ({ ...prev, email: true })),
          })}
          placeholder="Enter Email"
          type="email"
          autoComplete="none"
        />
      </div>

      <div className={styles.fieldInPut}>
        <Controller
          name="phone"
          control={control}
          rules={{
            required: "Phone number is required",
            validate: (value) =>
              validatePhoneNumber(value, touchedFields.phone),
          }}
          render={({ field }) => (
            <PhoneInput
              {...field}
              autoComplete="none"
              value={mobileNumber}
              international
              placeholder="Enter Phone Number"
              onFocus={() => {
                setIsFocused(true);
                setTouchedFields((prev) => ({ ...prev, phone: true }));
                if (phoneNumberError) {
                  setPhoneNumberError("");
                }
              }}
              onBlur={() => {
                setIsFocused(false);
                validatePhoneNumber(mobileNumber, touchedFields.phone);
                trigger("phone");
              }}
              countryCallingCodeEditable={false}
              onChange={(value) => {
                const phoneNumber = value ? value.toString() : "";
                setMobileNumber(phoneNumber);
                field.onChange(phoneNumber);
                validatePhoneNumber(phoneNumber, touchedFields.phone);
              }}
              onCountryChange={(country) => {
                handleCountryCodeChange(country);
                validatePhoneNumber(mobileNumber, touchedFields.phone);
              }}
              defaultCountry={geoLocationData?.countryCode}
            />
          )}
        />
      </div>
      {/* {isFocused && (
        <div className={styles.noticeText}>
          <small className={styles.info}>
            <Info />
            <span className="ms-1">
              OTP will be sent to your registered mobile number.
            </span>
          </small>
        </div>
      )} */}

      {errorMessage && <p className={styles.error}>{errorMessage}</p>}

      <div className={styles.field}>
        <button
          type="submit"
          disabled={isSubmitting || !isFormValid}
          className={`${styles.submitButton} ${
            isSubmitting ? styles.loading : ""
          } ${!isFormValid ? styles.disabled : ""}`}
        >
          {isSubmitting ? "Submitting..." : "Submit"}
        </button>
      </div>
    </form>
  );
};

export default InitialForm;
